import React, { useContext, useEffect, useRef, useState } from "react";

import MenuItem from "@material-ui/core/MenuItem";
import Menu from "@material-ui/core/Menu";
import { ListItemIcon, ListItemText } from "@material-ui/core";
import { Check, Replay, SwapHoriz, VisibilityOff, DeleteOutline, SettingsInputAntenna, Minimize, BusinessCenterOutlined } from "@material-ui/icons";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import TransferTicketModal from "../TransferTicketModal";
import TrocarConexaoModal from "../TrocarConexaoModal";
import toastError from "../../errors/toastError";
import { Can } from "../Can";
import { AuthContext } from "../../context/Auth/AuthContext";
import { FlutuantesAcoesContext } from "../../context/Flutuantes/FlutuantesContext";
import { useHistory } from "react-router-dom";

const useStyles = makeStyles(theme => ({
	menuItemFinalizar: {
		"&:hover": {
			backgroundColor: "rgba(13,148,136,0.12)",
			color: "#0D9488",
			"& .MuiListItemIcon-root": {
				color: "#0D9488"
			}
		},
	},
	menuItemRetornar: {
		"&:hover": {
			backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.06)" : "#f3f4f6",
		},
	},
	menuItemTransferir: {
		"&:hover": {
			backgroundColor: "rgba(33,150,243,0.1)",
			color: "#2196f3",
			"& .MuiListItemIcon-root": {
				color: "#2196f3"
			}
		},
	},
	menuItemConexao: {
		"&:hover": {
			backgroundColor: "rgba(13,148,136,0.1)",
			color: "#0D9488",
			"& .MuiListItemIcon-root": {
				color: "#0D9488"
			}
		},
	},
	menuItemOcultar: {
		"&:hover": {
			backgroundColor: "rgba(244,67,54,0.1)",
			color: "#f44336",
			"& .MuiListItemIcon-root": {
				color: "#f44336"
			}
		},
	},
	iconRoot: {
		minWidth: 36
	}
}));

const TIPOS_INTERNO = ["Funcionário", "Banco", "Parceiro", "Fornecedor", "Grupo", "Outro"];

// Tipo de contato direto pelo menu da conversa: candidato (I.A e fluxo
// atendem) ou contato interno (funcionario, banco... aba Internos).
const TipoContatoDialog = ({ aberto, onClose, contato }) => {
	const [interno, setInterno] = useState(false);
	const [tipo, setTipo] = useState("Funcionário");
	const [salvando, setSalvando] = useState(false);

	useEffect(() => {
		if (!aberto) return;
		setInterno(!!contato?.interno);
		setTipo(contato?.tipoInterno || "Funcionário");
	}, [aberto, contato]);

	const salvar = async () => {
		if (!contato?.id) return;
		setSalvando(true);
		try {
			await api.put(`/contacts/${contato.id}`, {
				name: contato.name,
				number: String(contato.number || "").replace(/\D/g, ""),
				interno,
				tipoInterno: interno ? tipo : null,
			});
			toast.success(interno ? `Marcado como contato interno (${tipo}).` : "Marcado como candidato.");
			onClose();
		} catch (err) {
			toastError(err);
		} finally {
			setSalvando(false);
		}
	};

	const cartao = (ativo) => ({
		flex: "1 1 200px", textAlign: "left", cursor: "pointer", borderRadius: 12, padding: "10px 14px",
		fontFamily: "inherit", background: ativo ? "rgba(13,148,136,.08)" : "transparent",
		border: ativo ? "2px solid #0d9488" : "1px solid rgba(15,23,42,.14)",
	});

	return (
		<Dialog open={aberto} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Tipo de contato</DialogTitle>
			<DialogContent dividers>
				<div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
					{[
						{ v: false, rotulo: "Candidato", sub: "A I.A e o fluxo atendem" },
						{ v: true, rotulo: "Contato interno", sub: "Funcionário, banco, parceiro… a I.A não responde" },
					].map((op) => (
						<button type="button" key={String(op.v)} style={cartao(interno === op.v)} onClick={() => setInterno(op.v)}>
							<div style={{ fontWeight: 800, fontSize: "0.9rem", color: interno === op.v ? "#0f766e" : "inherit" }}>{op.rotulo}</div>
							<div style={{ fontSize: "0.76rem", opacity: 0.7, marginTop: 2 }}>{op.sub}</div>
						</button>
					))}
				</div>
				{interno && (
					<TextField
						select
						label="Quem é"
						variant="outlined"
						size="small"
						fullWidth
						style={{ marginTop: 14 }}
						value={tipo}
						onChange={(e) => setTipo(e.target.value)}
						helperText="As conversas dele vão para a aba Internos e não contam como candidato."
					>
						{TIPOS_INTERNO.map((t) => (
							<MenuItem key={t} value={t}>{t}</MenuItem>
						))}
					</TextField>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={salvando}>Cancelar</Button>
				<Button onClick={salvar} color="primary" variant="contained" disabled={salvando} style={{ textTransform: "none", fontWeight: 800 }}>
					{salvando ? "Salvando..." : "Salvar"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

const TicketOptionsMenu = ({ ticket, menuOpen, handleClose, anchorEl, handleUpdateTicketStatus }) => {
	const classes = useStyles();
	const history = useHistory();
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [contactConfirmationOpen, setContactConfirmationOpen] = useState(false);
	const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
	const [trocarConexaoOpen, setTrocarConexaoOpen] = useState(false);
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);
	const { minimizarConversa } = useContext(FlutuantesAcoesContext) || {};

	// Vira uma bolinha flutuante (tipo Messenger) que acompanha o atendente
	// pelas outras páginas do sistema.
	const handleMinimizar = () => {
		handleClose();
		if (!minimizarConversa) return;
		minimizarConversa({
			id: ticket.id,
			nome: ticket.contact?.name,
			foto: ticket.contact?.profilePicUrl,
		});
		history.push("/atendimentos");
	};

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const handleDeleteContact = async () => {
		try {
			await api.delete(`/contacts/${ticket.contact.id}`);
			history.push("/atendimentos");
		} catch (err) {
			toastError(err);
		}
	};

	// Ocultar tira o atendimento da lista e nada mais: a conversa continua
	// inteira no banco e volta pela busca. E a mesma acao do menu da lista e a
	// que o sistema faz sozinho com os finalizados de mais de uma semana.
	const handleDeleteTicket = async () => {
		try {
			await api.put(`/tickets/${ticket.id}/ocultar`, { oculto: true });
			history.push("/atendimentos");
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenConfirmationModal = e => {
		setConfirmationOpen(true);
		handleClose();
	};

	const handleOpenTransferModal = e => {
		setTransferTicketModalOpen(true);
		handleClose();
	};

	const [tipoContatoOpen, setTipoContatoOpen] = useState(false);

	const handleOpenTrocarConexao = () => {
		setTrocarConexaoOpen(true);
		handleClose();
	};

	const handleCloseTransferTicketModal = () => {
		if (isMounted.current) {
			setTransferTicketModalOpen(false);
		}
	};

	return (
		<>
			<Menu
				id="menu-appbar"
				anchorEl={anchorEl}
				getContentAnchorEl={null}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				keepMounted
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				open={menuOpen}
				onClose={handleClose}
			>
				{}
				{ticket.status === "closed" && (
					<MenuItem
						onClick={(e) => {
							handleUpdateTicketStatus(e, "open", user?.id);
							handleClose();
						}}
						className={classes.menuItemFinalizar}
					>
						<ListItemIcon className={classes.iconRoot}>
							<Replay fontSize="small" />
						</ListItemIcon>
						<ListItemText primary={i18n.t("messagesList.header.buttons.reopen")} />
					</MenuItem>
				)}

				{(ticket.status === "open" || ticket.status === "pending") && (
					<>
						<MenuItem 
							onClick={(e) => {
								handleUpdateTicketStatus(e, "closed", user?.id);
								handleClose();
							}} 
							className={classes.menuItemFinalizar}
						>
							<ListItemIcon className={classes.iconRoot}>
								<Check fontSize="small" />
							</ListItemIcon>
							<ListItemText primary={i18n.t("messagesList.header.buttons.resolve")} />
						</MenuItem>
						
						<Can
							role={user.profile}
							perform="ticket-options:deleteTicket"
							yes={() => ticket.status === "open" && (
								<MenuItem 
									onClick={(e) => {
										handleUpdateTicketStatus(e, "pending", null);
										handleClose();
									}} 
									className={classes.menuItemRetornar}
								>
									<ListItemIcon className={classes.iconRoot}>
										<Replay fontSize="small" />
									</ListItemIcon>
									<ListItemText primary={i18n.t("messagesList.header.buttons.return")} />
								</MenuItem>
							)}
						/>
					</>
				)}

				<MenuItem onClick={handleMinimizar} className={classes.menuItemRetornar}>
					<ListItemIcon className={classes.iconRoot}>
						<Minimize fontSize="small" />
					</ListItemIcon>
					<ListItemText primary="Minimizar" />
				</MenuItem>

				<MenuItem onClick={handleOpenTransferModal} className={classes.menuItemTransferir}>
					<ListItemIcon className={classes.iconRoot}>
						<SwapHoriz fontSize="small" />
					</ListItemIcon>
					<ListItemText primary={i18n.t("ticketOptionsMenu.transfer")} />
				</MenuItem>

				{/*
					Trocar o numero pelo qual o atendimento continua. Serve para o
					atendimento que ficou sem conexao (a dele foi excluida) e tambem
					para mudar de numero no meio da conversa. Disponivel para todo
					mundo: quem esta com o cliente na mao e quem precisa resolver.
				*/}
				<MenuItem onClick={handleOpenTrocarConexao} className={classes.menuItemConexao}>
					<ListItemIcon className={classes.iconRoot}>
						<SettingsInputAntenna fontSize="small" />
					</ListItemIcon>
					<ListItemText primary="Trocar conexão" />
				</MenuItem>

				{!ticket.isGroup && !ticket.contact?.isGroup && (
					<MenuItem onClick={() => { setTipoContatoOpen(true); handleClose(); }} className={classes.menuItemConexao}>
						<ListItemIcon className={classes.iconRoot}>
							<BusinessCenterOutlined fontSize="small" />
						</ListItemIcon>
						<ListItemText
							primary="Tipo de contato"
							secondary={ticket.contact?.interno ? `Interno · ${ticket.contact?.tipoInterno || "Outro"}` : "Candidato"}
						/>
					</MenuItem>
				)}
				
				{/* Ocultar disponivel em qualquer status, inclusive na fila de espera */}
				<Can
					role={user.profile}
					perform="ticket-options:deleteTicket"
					yes={() => (
						<MenuItem onClick={handleOpenConfirmationModal} className={classes.menuItemOcultar}>
							<ListItemIcon className={classes.iconRoot}>
								<VisibilityOff fontSize="small" />
							</ListItemIcon>
							<ListItemText primary={i18n.t("ticketOptionsMenu.delete")} />
						</MenuItem>
					)}
				/>

				<Can
					role={user.profile}
					perform="ticket-options:deleteTicket"
					yes={() => (
						<MenuItem onClick={() => { setContactConfirmationOpen(true); handleClose(); }} className={classes.menuItemOcultar}>
							<ListItemIcon className={classes.iconRoot}>
								<DeleteOutline fontSize="small" />
							</ListItemIcon>
							<ListItemText primary="Deletar contato" />
						</MenuItem>
					)}
				/>
			</Menu>
			<ConfirmationModal
				title={`Ocultar o atendimento de ${ticket.contact?.name || "cliente"} (nº ${ticket.id})?`}
				open={confirmationOpen}
				onClose={setConfirmationOpen}
				onConfirm={handleDeleteTicket}
			>
				O atendimento sai da sua lista. A conversa continua guardada e volta
				pela busca, ou quando o cliente escrever de novo.
			</ConfirmationModal>
			<ConfirmationModal
				title={`Excluir Contato ${ticket.contact?.name || ""}?`}
				open={contactConfirmationOpen}
				onClose={setContactConfirmationOpen}
				onConfirm={handleDeleteContact}
			>
				Tem certeza que deseja excluir o contato permanentemente? Isso irá apagar todas as conversas e o cliente será removido da base.
			</ConfirmationModal>
			<TransferTicketModal
				modalOpen={transferTicketModalOpen}
				onClose={handleCloseTransferTicketModal}
				ticketid={ticket.id}
				ticket={ticket}
			/>
			<TipoContatoDialog
				aberto={tipoContatoOpen}
				onClose={() => { if (isMounted.current) setTipoContatoOpen(false); }}
				contato={ticket.contact}
			/>
			<TrocarConexaoModal
				modalOpen={trocarConexaoOpen}
				onClose={() => { if (isMounted.current) setTrocarConexaoOpen(false); }}
				ticketId={ticket.id}
				whatsappIdAtual={ticket.whatsappId}
			/>
		</>
	);
};

export default TicketOptionsMenu;
